/**
 * Utility functions for determining Köppen-Geiger climate classification
 * based on temperature and precipitation data.
 */

/**
 * Determines the Köppen-Geiger climate classification based on temperature and precipitation data
 * @param {Array} temperatureData - Array of daily temperature data objects with min, max, mean values
 * @param {Array} precipitationData - Array of daily precipitation data objects with min, max, mean values
 * @returns {Object} Climate classification information
 */
export const determineClimateClassification = (
  temperatureData,
  precipitationData
) => {
  if (
    !temperatureData ||
    !temperatureData.length ||
    !precipitationData ||
    !precipitationData.length
  ) {
    return {
      mainClass: "Unknown",
      subClass: "",
      fullName: "Insufficient data",
      description:
        "Climate classification requires complete temperature and precipitation data.",
    };
  }

  console.log("DEBUG: Temperature data sample:", temperatureData.slice(0, 5));
  console.log(
    "DEBUG: Precipitation data sample:",
    precipitationData.slice(0, 5)
  );

  // Check if we're using NASA POWER data (which has higher values)
  const isNasaData = precipitationData.some(
    (day) => day.mean !== undefined && day.mean < 10 && day.mean > 0 // NASA data is in mm/day, typically small values
  );
  console.log("DEBUG: Using NASA POWER precipitation data:", isNasaData);

  // Calculate monthly averages for temperature
  const monthlyTemps = calculateMonthlyAverages(temperatureData);
  console.log("DEBUG: Monthly temperature averages:", monthlyTemps);

  // Calculate annual average temperature
  const annualTemp =
    monthlyTemps.reduce((sum, month) => sum + month.mean, 0) / 12;

  // Find the warmest and coldest months
  const warmestMonth = Math.max(...monthlyTemps.map((m) => m.mean));
  const coldestMonth = Math.min(...monthlyTemps.map((m) => m.mean));

  // Calculate temperature range
  const tempRange = warmestMonth - coldestMonth;

  // Calculate precipitation pattern
  const monthlyPrecip = calculateMonthlyAverages(precipitationData);
  console.log("DEBUG: Monthly precipitation averages:", monthlyPrecip);

  // Calculate annual precipitation - handle NASA data differently
  let annualPrecip;
  if (isNasaData) {
    // NASA data is in mm/day, so we need to calculate the annual total
    // by summing the monthly averages and multiplying by days in each month
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Non-leap year

    // Calculate annual total by summing monthly totals (monthly average * days in month)
    const annualTotal = monthlyPrecip.reduce((sum, month, index) => {
      return sum + month.mean * daysInMonth[index];
    }, 0);

    annualPrecip = annualTotal;
    console.log(
      "DEBUG: Annual precipitation from NASA data (mm):",
      annualPrecip
    );
  } else {
    // For synthetic or EPW data, we use the average as before
    annualPrecip =
      monthlyPrecip.reduce((sum, month) => sum + month.mean, 0) / 12;
    console.log(
      "DEBUG: Annual precipitation from synthetic/EPW data (mm):",
      annualPrecip
    );
  }

  // Define seasons (Northern Hemisphere)
  // Summer: June, July, August (indices 5, 6, 7)
  // Winter: December, January, February (indices 11, 0, 1)
  const summerMonths = [5, 6, 7];
  const winterMonths = [11, 0, 1];

  // Calculate seasonal precipitation
  const summerPrecip = summerMonths.reduce(
    (sum, idx) => sum + monthlyPrecip[idx].mean,
    0
  );
  const winterPrecip = winterMonths.reduce(
    (sum, idx) => sum + monthlyPrecip[idx].mean,
    0
  );
  console.log("DEBUG: Summer precipitation (mm):", summerPrecip);
  console.log("DEBUG: Winter precipitation (mm):", winterPrecip);

  // Find driest and wettest months in each season
  const summerMonthlyPrecip = summerMonths.map(
    (idx) => monthlyPrecip[idx].mean
  );
  const winterMonthlyPrecip = winterMonths.map(
    (idx) => monthlyPrecip[idx].mean
  );

  const driestSummerMonth = Math.min(...summerMonthlyPrecip);
  const driestWinterMonth = Math.min(...winterMonthlyPrecip);
  const wettestSummerMonth = Math.max(...summerMonthlyPrecip);
  const wettestWinterMonth = Math.max(...winterMonthlyPrecip);
  console.log("DEBUG: Driest summer month (mm):", driestSummerMonth);
  console.log("DEBUG: Driest winter month (mm):", driestWinterMonth);
  console.log("DEBUG: Wettest summer month (mm):", wettestSummerMonth);
  console.log("DEBUG: Wettest winter month (mm):", wettestWinterMonth);

  // Determine main climate class
  let mainClass = "";
  let subClass = "";
  let fullName = "";
  let description = "";

  // Tropical climates (A)
  if (coldestMonth >= 18) {
    console.log("DEBUG: Evaluating as Tropical climate (A)");
    mainClass = "A";

    // Determine tropical subclass based on precipitation pattern
    const driestMonth = Math.min(...monthlyPrecip.map((m) => m.mean));
    const wettestMonth = Math.max(...monthlyPrecip.map((m) => m.mean));
    const annualTotal = monthlyPrecip.reduce(
      (sum, month) => sum + month.mean,
      0
    );

    console.log("DEBUG: Driest month precipitation:", driestMonth);
    console.log("DEBUG: Wettest month precipitation:", wettestMonth);
    console.log("DEBUG: Annual total precipitation:", annualTotal);

    // Af: Tropical rainforest - All months have at least 60mm of precipitation
    if (driestMonth >= 60) {
      subClass = "f";
      fullName = "Tropical rainforest climate";
      description =
        "Consistently hot and wet with high precipitation year-round.";
    }
    // Am: Tropical monsoon - Annual precipitation > 1500mm and driest month < 60mm but > 100 - (annual total/25)
    else if (
      annualTotal > 1500 &&
      driestMonth < 60 &&
      driestMonth > 100 - annualTotal / 25
    ) {
      subClass = "m";
      fullName = "Tropical monsoon climate";
      description =
        "Pronounced wet and dry seasons with high overall rainfall.";
    }
    // Aw: Tropical savanna - Distinct dry season
    else {
      subClass = "w";
      fullName = "Tropical savanna climate";
      description =
        "Distinct wet and dry seasons with warm temperatures year-round.";
    }
  }
  // Arid climates (B)
  else if (annualTemp > 0 && annualPrecip < 2 * annualTemp + 280) {
    console.log("DEBUG: Evaluating as Arid climate (B)");
    console.log(
      "DEBUG: Annual temp:",
      annualTemp,
      "Annual precip (mm):",
      annualPrecip,
      "Arid threshold (mm):",
      2 * annualTemp + 280
    );

    // For true Köppen classification:
    // BS (steppe): P > 50% of the threshold
    // BW (desert): P < 50% of the threshold
    const aridThreshold = 2 * annualTemp + 280;

    if (annualPrecip < aridThreshold * 0.5) {
      subClass = "W";
      fullName = "Hot desert climate";
      description =
        "Very hot, dry climate with minimal precipitation and large daily temperature variations.";
    } else {
      subClass = "S";
      fullName = "Cold semi-arid climate";
      description = "Semi-arid climate with hot summers and cold winters.";
    }

    // Add temperature modifier (h for hot, k for cold)
    if (annualTemp >= 18) {
      subClass += "h";
      if (subClass === "Wh") {
        fullName = "Hot desert climate";
      } else {
        fullName = "Hot semi-arid climate";
      }
    } else {
      subClass += "k";
      if (subClass === "Wk") {
        fullName = "Cold desert climate";
      } else {
        fullName = "Cold semi-arid climate";
      }
    }

    mainClass = "B";
  }
  // Temperate climates (C)
  else if (coldestMonth > 0 && coldestMonth < 18 && warmestMonth > 10) {
    console.log("DEBUG: Evaluating as Temperate climate (C)");
    mainClass = "C";

    // Calculate summer and winter precipitation
    const summerTotal = summerMonthlyPrecip.reduce((sum, val) => sum + val, 0);
    const winterTotal = winterMonthlyPrecip.reduce((sum, val) => sum + val, 0);

    // Determine precipitation pattern
    // s: dry summer - driest summer month < 30mm AND < 1/3 of wettest winter month
    // w: dry winter - driest winter month < 30mm AND < 1/10 of wettest summer month
    // f: no dry season - neither s nor w

    console.log("DEBUG: Summer total precipitation:", summerTotal);
    console.log("DEBUG: Winter total precipitation:", winterTotal);
    console.log("DEBUG: Driest summer month:", driestSummerMonth);
    console.log("DEBUG: Wettest winter month:", wettestWinterMonth);
    console.log("DEBUG: Driest winter month:", driestWinterMonth);
    console.log("DEBUG: Wettest summer month:", wettestSummerMonth);

    // Check for Mediterranean climate (dry summer)
    if (driestSummerMonth < 30 && driestSummerMonth < wettestWinterMonth / 3) {
      subClass = "s";
      fullName = "Mediterranean climate";
      description = "Warm to hot, dry summers and mild, wet winters.";
    }
    // Check for dry winter climate
    else if (
      driestWinterMonth < 30 &&
      driestWinterMonth < wettestSummerMonth / 10
    ) {
      subClass = "w";
      fullName = "Dry-winter subtropical climate";
      description = "Dry winters and wet summers with mild temperatures.";
    }
    // No pronounced dry season
    else {
      subClass = "f";
      fullName = "Oceanic climate";
      description =
        "Mild temperatures with no dry season and moderate rainfall throughout the year.";
    }

    // Add temperature modifier
    if (warmestMonth >= 22) {
      subClass += "a"; // Hot summer
      fullName += " with hot summers";
    } else if (
      warmestMonth < 22 &&
      monthlyTemps.filter((m) => m.mean >= 10).length >= 4
    ) {
      subClass += "b"; // Warm summer
      fullName += " with warm summers";
    } else {
      subClass += "c"; // Cool summer
      fullName += " with cool summers";
    }
  }
  // Continental climates (D)
  else if (coldestMonth <= 0 && warmestMonth > 10) {
    console.log("DEBUG: Evaluating as Continental climate (D)");
    mainClass = "D";

    // Calculate summer and winter precipitation
    const summerTotal = summerMonthlyPrecip.reduce((sum, val) => sum + val, 0);
    const winterTotal = winterMonthlyPrecip.reduce((sum, val) => sum + val, 0);

    // Determine precipitation pattern using the same rules as temperate climates
    console.log("DEBUG: Summer total precipitation:", summerTotal);
    console.log("DEBUG: Winter total precipitation:", winterTotal);

    // Check for dry summer continental
    if (driestSummerMonth < 30 && driestSummerMonth < wettestWinterMonth / 3) {
      subClass = "s";
      fullName = "Continental climate with dry summers";
      description =
        "Cold winters, warm summers, and a pronounced summer dry season.";
    }
    // Check for dry winter continental
    else if (
      driestWinterMonth < 30 &&
      driestWinterMonth < wettestSummerMonth / 10
    ) {
      subClass = "w";
      fullName = "Continental climate with dry winters";
      description =
        "Cold winters, warm summers, and a pronounced winter dry season.";
    }
    // No pronounced dry season
    else {
      subClass = "f";
      fullName = "Continental climate without dry season";
      description =
        "Cold winters, warm summers, and consistent precipitation throughout the year.";
    }

    // Add temperature modifier
    if (warmestMonth >= 22) {
      subClass += "a"; // Hot summer
      fullName += " and hot summers";
    } else if (
      warmestMonth < 22 &&
      monthlyTemps.filter((m) => m.mean >= 10).length >= 4
    ) {
      subClass += "b"; // Warm summer
      fullName += " and warm summers";
    } else {
      subClass += "c"; // Cool summer
      fullName += " and cool summers";
    }
  }
  // Polar climates (E)
  else if (warmestMonth <= 10) {
    console.log("DEBUG: Evaluating as Polar climate (E)");
    mainClass = "E";

    if (warmestMonth > 0) {
      subClass = "T";
      fullName = "Tundra climate";
      description =
        "Extremely cold winters, cool summers, and minimal precipitation.";
    } else {
      subClass = "F";
      fullName = "Ice cap climate";
      description =
        "Permanently frozen with temperatures below freezing year-round.";
    }
  }
  // Default case
  else {
    console.log("DEBUG: Climate does not fit standard classifications");
    mainClass = "Unknown";
    subClass = "";
    fullName = "Unclassified climate";
    description =
      "Climate data does not fit standard Köppen-Geiger classifications.";
  }

  console.log("DEBUG: Final classification:", mainClass + subClass, fullName);

  return {
    mainClass,
    subClass,
    fullName,
    description,
    annualTemp: annualTemp.toFixed(1),
    tempRange: tempRange.toFixed(1),
    annualPrecipitation: annualPrecip.toFixed(1),
  };
};

/**
 * Calculates monthly averages from daily data
 * @param {Array} dailyData - Array of daily data objects
 * @returns {Array} Array of monthly average objects
 */
const calculateMonthlyAverages = (dailyData) => {
  const monthlyData = Array(12)
    .fill()
    .map(() => ({
      min: 0,
      max: 0,
      mean: 0,
      count: 0,
    }));

  // Group data by month
  dailyData.forEach((day) => {
    const month = day.month - 1; // 0-based month index
    monthlyData[month].min += day.min;
    monthlyData[month].max += day.max;
    monthlyData[month].mean += day.mean;
    monthlyData[month].count += 1;
  });

  // Calculate averages
  monthlyData.forEach((month) => {
    if (month.count > 0) {
      month.min /= month.count;
      month.max /= month.count;
      month.mean /= month.count;
    }
  });

  return monthlyData;
};

/**
 * Returns a color associated with the climate classification
 * @param {string} mainClass - The main climate class (A, B, C, D, E)
 * @returns {string} A hex color code
 */
export const getClimateColor = (mainClass) => {
  const colors = {
    A: "#0000FF", // Blue for tropical
    B: "#FF0000", // Red for arid
    C: "#00FF00", // Green for temperate
    D: "#FFA500", // Orange for continental
    E: "#808080", // Purple for polar
    Unknown: "#808080", // Gray for unknown
  };

  return colors[mainClass] || colors["Unknown"];
};

/**
 * Returns a color associated with the specific Köppen climate classification
 * @param {string} mainClass - The main climate class (A, B, C, D, E)
 * @param {string} subClass - The climate subclass
 * @returns {string} A hex color code
 */
export const getDetailedClimateColor = (mainClass, subClass = "") => {
  // Colors from the Köppen-Geiger Data overall.csv file
  const detailedColors = {
    // A - Tropical
    Af: "#1300FC", // Tropical Rainforest
    Am: "#0E73FC", // Tropical Monsoon
    As: "#3AAAFC", // Tropical Savanna, Dry Summer
    Aw: "#3AAAFC", // Tropical Savanna, Dry Winter

    // B - Arid
    BSh: "#F6A200", // Hot Semi-Arid
    BSk: "#FDDA62", // Cold Semi-Arid
    BWh: "#FD0000", // Hot Desert
    BWk: "#FE9594", // Cold Desert

    // C - Temperate
    Cfa: "#C5FE4B", // Humid Subtropical
    Cfb: "#63FD32", // Temperate Oceanic
    Cfc: "#35C600", // Subpolar Oceanic
    Cwa: "#94FE97", // Monsoon-influenced humid subtropical
    Cwb: "#5FC765", // Subtropical Highland
    Cwc: "#369633", // Cold Subtropical Highland
    Csa: "#FCFE04", // Hot-summer Mediterranean
    Csb: "#CECC08", // Warm-summer Mediterranean

    // D - Continental
    Dfa: "#00FCFD", // Hot-summer Humid Continental
    Dfb: "#3DC6FA", // Warm-summer Humid Continental
    Dfc: "#007E7E", // Subarctic
    Dfd: "#004660", // Extremely Cold Subarctic
    Dwa: "#A5AFFF", // Monsoon-influenced Hot-summer Humid Continental
    Dwb: "#4A78E3", // Monsoon-influenced Warm-summer Humid Continental
    Dwc: "#484EB4", // Monsoon-influenced Subarctic
    Dwd: "#30008A", // Monsoon-influenced Extremely Cold Subarctic
    Dsa: "#FC00FB", // Mediterranean-influenced Hot-summer Humid Continental
    Dsb: "#C900C4", // Mediterranean-influenced Warm-summer Humid Continental
    Dsc: "#983396", // Mediterranean-influenced Subarctic

    // E - Polar
    ET: "#AEB0AD", // Tundra
    EF: "#686967", // Ice Cap

    // Default fallback colors by main class
    A: "#3AAAFC", // Default blue for tropical
    B: "#F6A200", // Default orange for arid
    C: "#63FD32", // Default green for temperate
    D: "#3DC6FA", // Default cyan for continental
    E: "#AEB0AD", // Default gray for polar
    Unknown: "#808080", // Gray for unknown
  };

  // Try to get the specific color for the full classification
  const fullClass = mainClass + subClass;

  // First check if we have an exact match for the full classification
  if (detailedColors[fullClass]) {
    return detailedColors[fullClass];
  }

  // If no exact match, try to match just the first two characters (e.g., "BS" from "BSh")
  if (fullClass.length >= 2) {
    const prefix = fullClass.substring(0, 2);
    if (detailedColors[prefix]) {
      return detailedColors[prefix];
    }
  }

  // If no match found, fall back to the main class color
  return detailedColors[mainClass] || detailedColors["Unknown"];
};
